<template>
  <div class="paragraph-grid-column flex h-full flex-col" :class="spanClass">
    <BlokkliField
      class="flex h-full flex-col gap-spacing-base"
      :class="
        isEditing && 'min-h-48 rounded border-2 border-dashed border-border p-3'
      "
      name="field_paragraphs"
      :list="filterNullArray(paragraphs)"
      :should-render-item
    />
  </div>
</template>

<script lang="ts" setup>
import type { ParagraphGridColumnFragment } from '#graphql-operations'
import { filterNullArray } from '~/helpers'

type GridColumnWidth =
  | 'full'
  | 'three_quarters'
  | 'two_thirds'
  | 'half'
  | 'third'
  | 'quarter'
  | 'sixth'

defineProps<{
  paragraphs?: ParagraphGridColumnFragment['paragraphs']
}>()

const isEditing = false

const { options } = defineBlokkli({
  bundle: 'grid_column',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
  options: {
    width: {
      type: 'radios',
      label: 'Width',
      default: 'half',
      options: {
        full: 'Full',
        three_quarters: '3/4',
        two_thirds: '2/3',
        half: '1/2',
        third: '1/3',
        quarter: '1/4',
        sixth: '1/6',
      },
    },
  },
  editor: {
    disableEdit: true,
  },
  propsFieldMapping: {
    paragraphs: { type: 'field', name: 'field_paragraphs' },
  },
})

const SPAN_CLASS: Record<GridColumnWidth, string> = {
  full: 'col-span-12',
  three_quarters: 'col-span-9',
  two_thirds: 'col-span-8',
  half: 'col-span-6',
  third: 'col-span-4',
  quarter: 'col-span-3',
  sixth: 'col-span-2',
}

// The span in twelfths, passed down so nested images know how wide they are
// actually rendered and can request a matching source.
const SPAN: Record<GridColumnWidth, number> = {
  full: 12,
  three_quarters: 9,
  two_thirds: 8,
  half: 6,
  third: 4,
  quarter: 3,
  sixth: 2,
}

const width = computed<GridColumnWidth>(
  () => (options.value.width as GridColumnWidth) || 'half',
)

const spanClass = computed(() => SPAN_CLASS[width.value])

const { shouldRenderItem } = useParagraphVisiblity()

provideParagraphContext(
  'gridColumnSpan',
  computed(() => SPAN[width.value]),
)
</script>
